import {
  FetchParams,
  HttpParams,
  MutationParams,
  MutationParamsFormData,
  QueryParams,
} from '@domain/types';
import { AUTH_APP_NAME, LOGIN_PARTITION } from '@launcher/constants';
import { isDesktopApp, isUsingNewElectron } from '@launcher/utils/desktop';
import { tracking } from '@services/analytics';
import { electronService } from '@services/electron/electron.service';
import { EnvManager } from '@services/environment/environment.service';
import envVariables from '@services/environment/variables';
import { intercom } from '@services/intercom';
import { logger } from '@services/logger/logger.service';
import { generateTraceContext } from '@services/tracing/tracing.service';
import { saveAs } from 'file-saver';

import { HttpError } from './http.error.service';

const BASE_URL = `${window.location.protocol}//${window.location.host}`;
const CSRF_HEADER = 'Authenticity-Token';
const CSRF_ERROR_CODE = `Can't verify CSRF token authenticity`;
const MAX_CSRF_RETRIES = 3;

export class Http {
  private csrfToken = '';

  private csrfRetries = 0;

  private get version() {
    return 'v1';
  }

  private static instance: Http;

  public static getInstance(): Http {
    if (!Http.instance) {
      Http.instance = new Http();
    }
    return Http.instance;
  }

  public setCsrfToken(value: string): void {
    this.csrfToken = value;
    logger.setCsrfToken(value);
  }

  private getHeaders(
    params: { noCache: boolean; contentType?: string } = {
      noCache: false,
      contentType: 'application/json',
    },
  ) {
    const headers: Record<string, string> = {
      'Content-Type': params.contentType ?? 'application/json',
      'X-CSRF-Token': this.csrfToken,
      'X-Auth-Mode': 'session',
    };

    if (params.contentType === 'multipart/form-data') {
      headers.enctype = 'multipart/form-data';
      delete headers['Content-Type'];
    }

    if (params.noCache) {
      headers['Cache-Control'] = 'no-store';
    }
    return headers;
  }

  private readonly getSearchParams = (searchParams: QueryParams): string => {
    const params: URLSearchParams = new URLSearchParams();

    for (const [key, value] of Object.entries(searchParams)) {
      if ((typeof value === 'string' || Array.isArray(value)) && value.length === 0) {
        continue;
      }

      if (Array.isArray(value)) {
        value.forEach((v) => params.append(`${key}[]`, v));
      } else {
        params.set(key, String(value));
      }
    }

    return params.toString();
  };

  get baseUrl(): string {
    let host = EnvManager.getBaseUrlBackend();

    if (envVariables.NODE_ENV === 'test') {
      host = 'http://localhost';
    }

    if (host === '') {
      host = BASE_URL;
    }
    return `${host}/api/`;
  }

  private getURL(params: FetchParams): URL {
    let location = `${this.baseUrl}${params.version ?? this.version}${params.path}`;
    if (params.searchParams) {
      const searchParams = this.getSearchParams(params.searchParams);
      location += `?${searchParams}`;
    }
    return new URL(location);
  }

  private async fetchResponse({
    method,
    params,
    headerOptions,
    bodyParams,
  }: {
    method: string;
    params: FetchParams;
    headerOptions?: { noCache: boolean; contentType?: string };
    bodyParams?: BodyInit;
  }): Promise<{ header: Headers; data: any }> {
    const url = this.getURL(params);

    const { traceparent, ...traceContext } = generateTraceContext();

    logger.info('HTTP', `REQ - ${method} ${url.pathname}`, {
      ...traceContext,
      method,
      host: url.host,
      path: url.pathname,
    });

    const baseHeaders = this.getHeaders(headerOptions);
    const initRequest: RequestInit = bodyParams
      ? {
          method,
          headers: { ...baseHeaders, traceparent },
          body: bodyParams,
          credentials: 'include',
        }
      : {
          method,
          headers: { ...baseHeaders, traceparent },
          credentials: 'include',
        };

    let response;

    try {
      response = await fetch(url.toString(), initRequest);
    } catch (error) {
      logger.error('HTTP', `Failed to fetch ${method} ${url.pathname}`, error);
      throw new HttpError({ message: 'Failed to fetch', statusCode: 0 });
    }

    logger.info('HTTP', `RESP - ${method} ${url.pathname}`, {
      ...traceContext,
      method,
      host: url.host,
      path: url.pathname,
      status: response.status,
    });

    if (response?.status === 401) {
      this.onUnauthorized(response);
    }

    const responseContentType = response.headers.get('content-type');
    let responseData;
    // TODO https://honestica.atlassian.net/browse/SEDEX-1421
    // should be handled but breaks some tests
    // if (responseContentType && /text\/plain/.exec(responseContentType.toLowerCase())) {
    //   responseData = response.text();
    // } else if (
    if (
      responseContentType &&
      /^application\/pdf|application\/zip|application\/octet-stream/.exec(
        responseContentType.toLowerCase(),
      )
    ) {
      responseData = await response.blob();
    } else {
      // in case of empty response .json() will fail so returns undefined
      responseData = await response.json().catch(() => undefined);
    }

    if (response?.status === 422 && responseData?.error === CSRF_ERROR_CODE) {
      await this.fetchCsrfToken();

      // Retry the request with the new CSRF token, max 3 times
      if (this.csrfRetries < MAX_CSRF_RETRIES) {
        this.csrfRetries += 1;
        return this.fetchResponse({ method, params, headerOptions, bodyParams });
      }
    }

    this.csrfRetries = 0;

    if (response?.status >= 400) {
      throw new HttpError({
        message: responseData?.message || response.statusText,
        statusCode: response.status,
        key: responseData?.key,
        headers: response.headers,
        response: responseData,
      });
    }

    return { header: response.headers, data: responseData };
  }

  public async get(params: FetchParams, noCache = false) {
    const method = 'GET';
    return (await this.fetchResponse({ method, params, headerOptions: { noCache } })).data;
  }

  private async mutate<T>(
    params: MutationParams,
    method: 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  ): Promise<T> {
    return (await this.fetchResponse({ method, params, bodyParams: JSON.stringify(params.body) }))
      .data;
  }

  public async post<T>(params: MutationParams) {
    return await this.mutate<T>(params, 'POST');
  }

  public async download(params: FetchParams | MutationParams, method: 'GET' | 'POST' = 'GET') {
    const bodyParams = 'body' in params ? JSON.stringify(params.body) : undefined;
    const response = await this.fetchResponse({ method, params, bodyParams });
    const filename = response.header.get('filename') || 'document';
    saveAs(response.data, filename);
  }

  public async put<T>(params: MutationParams) {
    return await this.mutate<T>(params, 'PUT');
  }

  public async patch<T>(params: MutationParams) {
    return await this.mutate<T>(params, 'PATCH');
  }

  public async postMultiFormData(params: MutationParamsFormData) {
    const method = 'POST';
    return (
      await this.fetchResponse({
        method,
        params,
        headerOptions: {
          noCache: false,
          contentType: 'multipart/form-data',
        },
        bodyParams: params.body,
      })
    ).data;
  }

  public async destroy(params: HttpParams) {
    const method = 'DELETE';
    return (await this.fetchResponse({ method, params })).data;
  }

  public async destroyWithBody<T>(params: MutationParams) {
    return await this.mutate<T>(params, 'DELETE');
  }

  public fetchCsrfToken = async () => {
    const url = this.getURL({ path: `/authenticity_tokens` });

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: this.getHeaders({ noCache: true }),
      credentials: 'include',
    });

    if (response.status === 401) {
      this.onUnauthorized(response);
    }

    this.csrfToken = this.getCsrfTokenFromHeaders(response);
  };

  public getCsrfTokenFromHeaders(response: Response) {
    return response.headers.get(CSRF_HEADER) || '';
  }

  public async logout() {
    const logoutUrl = `${
      EnvManager.getName() === 'review'
        ? EnvManager.getBaseUrlSending()
        : EnvManager.getBaseUrlBackend()
    }/lifen_connect/logout?app_name=${AUTH_APP_NAME}&origin=${EnvManager.getUrlLoginCallback()}`;

    // Intercom cookies outlive the page: without this the next user on a shared workstation
    // inherits the previous one's conversation history.
    intercom.shutdown();

    if (isDesktopApp()) {
      await this.clearCache();
      await electronService.logout(logoutUrl);
    } else {
      window.location.replace(logoutUrl);
    }
  }

  public async redirectToLogin(withWorkspaceId = false) {
    if (isDesktopApp()) {
      const isNewElectron = await isUsingNewElectron();
      if (isNewElectron) {
        // New electron behavior: logout and reload login
        await this.logout();
      } else {
        window.location.reload();
      }
    } else {
      const loginMessage = localStorage.getItem('login-message');
      localStorage.removeItem('login-message');
      const loginMessageParams: string = loginMessage ? `&alert=${loginMessage}` : '';

      let url = `${
        EnvManager.getName() === 'review'
          ? EnvManager.getBaseUrlSending()
          : EnvManager.getBaseUrlBackend()
      }/lifen_connect/login?app_name=${AUTH_APP_NAME}&origin=${window.location.protocol}//${
        window.location.host
      }${loginMessageParams}`;

      const workspaceId = localStorage.getItem('WORKSPACE_ID');

      if (workspaceId && withWorkspaceId === true) {
        url = `${url}&workspace_id=${workspaceId}`;
      }

      window.location.replace(url);
    }
  }

  private async clearCache() {
    /* We access the session via the partition name
     * and clear localStorage data in order to manually
     * disconnect the user on next reload
     */

    await window.electron?.clearCache(LOGIN_PARTITION);
    await window.electron?.clearSessionStorageData({
      partition: LOGIN_PARTITION,
      options: {
        origin: `https://${EnvManager.getAuth0Domain()}`,
      },
    });

    await window.electron?.clearSessionStorageData({
      partition: LOGIN_PARTITION,
      options: {
        origin: `${window.location.protocol}//${window.location.host}`,
        storages: ['cookies'],
      },
    });
  }

  private async onUnauthorized(response: Response) {
    tracking.userForcedLogout();
    const responseData = await response.json();

    if (response.status === 401 && responseData.status === 'Unauthorized (no session found)') {
      this.redirectToLogin();
    } else {
      localStorage.setItem('login-message', responseData.error_message);
      this.logout();
    }
  }
}
